import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, API_PATIENT_LIST} from "../../../../helpers/urlHelper";

const API_PATIENTS_LIST = `${API_BASE_URL}${API_PATIENT_LIST}`;

const PatientSelection = ({ initialPatientData, onPatientSelect }) => {
    const [patients, setPatients] = useState([]);
    const [shown, setShown] = useState([]);
    const [query, setQuery] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [hasInitialData, setHasInitialData] = useState(false);

    // Efecto para manejar datos iniciales del paciente
    useEffect(() => {
        if (initialPatientData && initialPatientData.patientId) {
            setHasInitialData(true);
            setSelectedId(initialPatientData.patientId);
            setSelectedPatient({
                _id: initialPatientData.patientId,
                name: initialPatientData.patientName?.split(' ')[0] || 'N/A',
                surname: initialPatientData.patientName?.split(' ').slice(1).join(' ') || '',
                email: initialPatientData.patientEmail,
                phone: initialPatientData.patientPhone,
                dateOfBirth: initialPatientData.patientDob
            });
        }
    }, [initialPatientData]);

    useEffect(() => {
        // Solo cargar pacientes si no hay datos iniciales
        if (!hasInitialData) {
            axios.get(API_PATIENTS_LIST)
                .then(response => {
                    const patientsFromApi = response.data.data;
                    setPatients(patientsFromApi);
                    setShown(patientsFromApi);
                })
                .catch(err => console.error('Error cargando pacientes:', err));
        }
    }, [hasInitialData]);

    // Efecto para búsqueda en tiempo real
    useEffect(() => {
        if (patients.length > 0) {
            handleSearch();
        }
    }, [query, patients]);

    const handleSearch = () => {
        const q = query.trim().toLowerCase();
        if (q === '') {
            setShown(patients);
        } else {
            const filteredPatients = patients.filter(u => {
                const fullName = `${u.name || ''} ${u.surname || ''}`.toLowerCase();
                const email = (u.email || '').toLowerCase();
                const phone = (u.phone || '').toLowerCase();
                
                return fullName.includes(q) || 
                       email.includes(q) || 
                       phone.includes(q);
            });
            setShown(filteredPatients);
        }
    };

    const handleQueryChange = (e) => {
        setQuery(e.target.value);
        // La búsqueda se ejecutará automáticamente por el useEffect
    };

    const handlePatientSelect = (patient) => {
        setSelectedId(patient._id);
        setSelectedPatient(patient);

        if (onPatientSelect) {
            onPatientSelect(patient);
        }
    };

    const clearSelection = () => {
        setSelectedId(null);
        setSelectedPatient(null);
        setHasInitialData(false);
        setQuery(''); // Limpiar también la búsqueda
        // Cargar pacientes para búsqueda
        axios.get(API_PATIENTS_LIST)
            .then(response => {
                const patientsFromApi = response.data.data;
                setPatients(patientsFromApi);
                setShown(patientsFromApi);
            })
            .catch(err => console.error('Error cargando pacientes:', err));
    };

    // Si hay datos iniciales, mostrar el paciente seleccionado
    if (hasInitialData && selectedPatient) {
        const fullName = `${selectedPatient.name} ${selectedPatient.surname}`;
        const initials = fullName
            .split(' ')
            .filter(w => w.length > 0)
            .map(w => w[0])
            .slice(0, 2)
            .join('')
            .toUpperCase();

        return (
            <div className="card shadow-sm mb-4">
                <div className="card-body p-4">
                    <div className="d-flex align-items-center mb-4">
                        <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3" 
                             style={{width: '40px', height: '40px', fontSize: '1.2rem'}}>
                            1
                        </div>
                        <h5 className="mb-0 text-primary fw-semibold">Selección del paciente</h5>
                    </div>

                    <div className="mt-3">
                        <div className="card border-primary bg-light">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle bg-primary bg-opacity-25 text-primary d-flex align-items-center justify-content-center me-3 fw-bold" 
                                         style={{width: '50px', height: '50px', fontSize: '1.5rem'}}>
                                        {initials}
                                    </div>
                                    <div className="flex-grow-1">
                                        <div className="fw-semibold fs-5 mb-2">{fullName}</div>
                                        {selectedPatient.email && (
                                            <div className="text-secondary small mb-1">Email: {selectedPatient.email}</div>
                                        )}
                                        {selectedPatient.phone && (
                                            <div className="text-secondary small mb-1">Teléfono: {selectedPatient.phone}</div>
                                        )}
                                        {selectedPatient.dateOfBirth && (
                                            <div className="text-secondary small mb-1">F. Nacimiento: {selectedPatient.dateOfBirth}</div>
                                        )}
                                    </div>
                                    <div className="ms-3">
                                        <button className="btn btn-success btn-sm me-2">
                                            ✓ Seleccionado
                                        </button>
                                        <button className="btn btn-secondary btn-sm" onClick={clearSelection}>
                                            Cambiar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Si no hay datos iniciales, mostrar búsqueda normal
    return (
        <div className="card shadow-sm mb-4">
            <div className="card-body p-4">
                <div className="d-flex align-items-center mb-4">
                    <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3" 
                         style={{width: '40px', height: '40px', fontSize: '1.2rem'}}>
                        1
                    </div>
                    <h5 className="mb-0 text-primary fw-semibold">Selección del paciente</h5>
                </div>

                <div className="input-group mb-3">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar nombre, email o teléfono del paciente…"
                        value={query}
                        onChange={handleQueryChange}
                        onKeyDown={e => e.key === 'Enter' && handleSearch()}
                    />
                    <button className="btn btn-primary" onClick={handleSearch}>
                        Buscar
                    </button>
                </div>

                <div style={{maxHeight: '285px', overflowY: 'auto'}}>
                    {shown.map(u => {
                        const fullName = `${u.name || ''} ${u.surname || ''}`.trim();
                        const initials = fullName
                            .split(' ')
                            .filter(w => w.length > 0)
                            .map(w => w[0])
                            .slice(0, 2)
                            .join('')
                            .toUpperCase();

                        const isSelected = selectedId === u._id;

                        return (
                            <div
                                key={u._id}
                                className={`card mb-3 ${isSelected ? 'border-primary bg-light' : 'border'}`}
                                style={{cursor: 'pointer', transition: 'all 0.2s'}}
                                onClick={() => handlePatientSelect(u)}
                                onMouseEnter={(e) => {
                                    if (!isSelected) {
                                        e.currentTarget.classList.add('border-primary', 'bg-light');
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) {
                                        e.currentTarget.classList.remove('border-primary', 'bg-light');
                                    }
                                }}
                            >
                                <div className="card-body p-3">
                                    <div className="d-flex align-items-center">
                                        <div className="rounded-circle bg-primary bg-opacity-25 text-primary d-flex align-items-center justify-content-center me-3 fw-bold" 
                                             style={{width: '50px', height: '50px', fontSize: '1.5rem'}}>
                                            {initials || '??'}
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="fw-semibold fs-6 mb-1">{fullName || 'Sin nombre'}</div>
                                            {u.email && (
                                                <div className="text-secondary small">Email: {u.email}</div>
                                            )}
                                            {u.phone && (
                                                <div className="text-secondary small">Teléfono: {u.phone}</div>
                                            )}
                                        </div>
                                        <div className="ms-3">
                                            <button className="btn btn-primary btn-sm">
                                                {isSelected ? '✓ Seleccionado' : 'Seleccionar'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {shown.length === 0 && patients.length > 0 && (
                        <div className="text-center text-secondary fst-italic py-4">
                            No se encontraron pacientes con ese criterio de búsqueda.
                        </div>
                    )}

                    {shown.length === 0 && patients.length === 0 && (
                        <div className="text-center text-secondary fst-italic py-4">
                            Cargando pacientes...
                        </div>
                    )}
                </div>

                {shown.length > 0 && (
                    <div className="alert alert-primary mt-3 mb-0 text-center small">
                        Mostrando {shown.length} paciente{shown.length !== 1 ? 's' : ''}
                        {query && (
                            <span> de {patients.length} total{patients.length !== 1 ? 'es' : ''}</span>
                        )}
                        {shown.length > 3 && (
                            <span className="fw-medium"> - Desplázate para ver más</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PatientSelection;